import { useState } from 'react';
import { StatsCard } from './StatsCard';
import { formatDate } from '../../utils/formatDate';
import { MARKER_COLORS, PERSON_COLORS } from '../../utils/markerColor';
import type { Place, User, CountryVisit, VisitType } from '../../types';

type ActiveView = 'countries' | 'places' | 'mark' | 'anya' | 'victoria' | 'liam' | 'family' | 'group' | 'markanya' | null;

interface SidebarProps {
  user: User | null;
  places: Place[];
  countries: CountryVisit[];
  onPlaceClick: (place: Place) => void;
  onCountryClick?: (countryCode: string) => void;
  availableYears: number[];
  selectedYear: number | null;
  onYearChange: (year: number | null) => void;
}

const FLAG_BASE = 'https://flagcdn.com/20x15';

function CountryList({ countries, places, onCountryClick }: { countries: CountryVisit[]; places: Place[]; onCountryClick?: (countryCode: string) => void }) {
  const countryNames: Record<string, string> = {};
  places.forEach((p) => { if (p.CountryCode) countryNames[p.CountryCode] = p.Country; });

  const sorted = [...countries].sort((a, b) => {
    const order: Record<VisitType, number> = { markanya: 0, family: 1, group: 2, solo: 3 };
    return order[a.visitType] - order[b.visitType];
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {sorted.map((c) => (
        <div
          key={c.countryCode}
          onClick={() => onCountryClick?.(c.countryCode)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}
        >
          <img
            src={`${FLAG_BASE}/${c.countryCode.toLowerCase()}.png`}
            alt={c.countryCode}
            style={{ width: 20, height: 15, flexShrink: 0 }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <span style={{ flex: 1, fontSize: 13 }}>{countryNames[c.countryCode] || c.countryCode}</span>
          <span style={{
            width: 10, height: 10, borderRadius: '50%',
            background: MARKER_COLORS[c.visitType],
            flexShrink: 0,
          }} />
        </div>
      ))}
    </div>
  );
}

function PlaceList({ places, filter, personFilter, onPlaceClick }: { places: Place[]; filter?: VisitType; personFilter?: string; onPlaceClick: (place: Place) => void }) {
  const filtered = personFilter
    ? places.filter((p) => p.myVisitType === 'solo' && p.mySoloTraveller === personFilter)
    : filter ? places.filter((p) => p.myVisitType === filter) : places.filter((p) => p.myVisitType != null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {filtered.map((p) => (
        <div
          key={p.PlaceId}
          onClick={() => onPlaceClick(p)}
          style={{ padding: '5px 0', borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              background: p.myVisitType === 'solo' && p.mySoloTraveller && PERSON_COLORS[p.mySoloTraveller]
                ? PERSON_COLORS[p.mySoloTraveller]
                : p.myVisitType ? MARKER_COLORS[p.myVisitType] : '#9e9e9e',
            }} />
            <span style={{ fontWeight: 500, fontSize: 13 }}>{p.PlaceName}</span>
          </div>
          <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 1, paddingLeft: 14 }}>
            {p.Country}{p.myVisitDate ? ` · ${formatDate(p.myVisitDate)}` : ''}
          </div>
        </div>
      ))}
      {filtered.length === 0 && <div style={{ fontSize: 13, color: '#bdbdbd' }}>None yet.</div>}
    </div>
  );
}

export function Sidebar({ user, places, countries, onPlaceClick, onCountryClick, availableYears, selectedYear, onYearChange }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [activeView, setActiveView] = useState<ActiveView>(null);

  const myPlaces = places.filter((p) => p.myVisitType != null);
  const markCount     = myPlaces.filter((p) => p.myVisitType === 'solo' && p.mySoloTraveller === 'Mark').length;
  const anyaCount     = myPlaces.filter((p) => p.myVisitType === 'solo' && p.mySoloTraveller === 'Anya').length;
  const victoriaCount = myPlaces.filter((p) => p.myVisitType === 'solo' && p.mySoloTraveller === 'Victoria').length;
  const liamCount     = myPlaces.filter((p) => p.myVisitType === 'solo' && p.mySoloTraveller === 'Liam').length;
  const familyCount   = myPlaces.filter((p) => p.myVisitType === 'family').length;
  const groupCount    = myPlaces.filter((p) => p.myVisitType === 'group').length;
  const markanyaCount = myPlaces.filter((p) => p.myVisitType === 'markanya').length;

  const toggle = (view: ActiveView) => setActiveView((v) => (v === view ? null : view));

  const STATS = [
    { key: 'countries' as ActiveView, label: 'Countries',    value: countries.length, color: '#4CAF50' },
    { key: 'places'    as ActiveView, label: 'Places',       value: myPlaces.length,  color: '#2196F3' },
    { key: 'mark'      as ActiveView, label: 'Mark',           value: markCount,        color: PERSON_COLORS.Mark },
    { key: 'anya'      as ActiveView, label: 'Anya',           value: anyaCount,        color: PERSON_COLORS.Anya },
    { key: 'victoria'  as ActiveView, label: 'Victoria',       value: victoriaCount,    color: PERSON_COLORS.Victoria },
    { key: 'liam'      as ActiveView, label: 'Liam',           value: liamCount,        color: PERSON_COLORS.Liam },
    { key: 'family'    as ActiveView, label: 'Family trips',   value: familyCount,      color: MARKER_COLORS.family },
    { key: 'group'     as ActiveView, label: 'Group trips',    value: groupCount,       color: MARKER_COLORS.group },
    { key: 'markanya'  as ActiveView, label: 'Mark & Anya',    value: markanyaCount,    color: MARKER_COLORS.markanya },
  ];

  return (
    <div style={{
      position: 'absolute',
      top: 54,
      left: 0,
      height: 'calc(100% - 54px)',
      width: collapsed ? 40 : 240,
      background: '#f8f9fa',
      boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
      transition: 'width 0.25s ease',
      zIndex: 10,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <button
        onClick={() => { setCollapsed(!collapsed); if (!collapsed) setActiveView(null); }}
        title={collapsed ? 'Expand' : 'Collapse'}
        style={{
          alignSelf: 'flex-end', margin: 8, background: 'none',
          border: '1px solid #e0e0e0', borderRadius: 6,
          width: 26, height: 26, cursor: 'pointer', fontSize: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        {collapsed ? '›' : '‹'}
      </button>

      {!collapsed && (
        <div style={{ padding: '0 10px 16px', overflowY: 'auto', flex: 1 }}>
          {user && (
            <div style={{ marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #e0e0e0' }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{user.DisplayName}</div>
              {user.Email && <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 1 }}>{user.Email}</div>}
            </div>
          )}

          {availableYears.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <select
                value={selectedYear ?? ''}
                onChange={(e) => onYearChange(e.target.value ? Number(e.target.value) : null)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  fontSize: 13,
                  borderRadius: 6,
                  border: '1px solid #e0e0e0',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                <option value="">All years</option>
                {availableYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {STATS.map(({ key, label, value, color }) => (
              <div key={key}>
                <div
                  onClick={() => toggle(key)}
                  style={{ cursor: 'pointer' }}
                  title={`Click to ${activeView === key ? 'hide' : 'show'} ${label.toLowerCase()}`}
                >
                  <StatsCard
                    label={label}
                    value={value}
                    color={color}
                    active={activeView === key}
                  />
                </div>

                {activeView === key && (
                  <div style={{
                    background: '#fff',
                    border: '1px solid #e8e8e8',
                    borderRadius: '0 0 8px 8px',
                    padding: '8px 10px',
                    maxHeight: 220,
                    overflowY: 'auto',
                    marginTop: -2,
                  }}>
                    {key === 'countries'
                      ? <CountryList countries={countries} places={places} onCountryClick={onCountryClick} />
                      : ['mark', 'anya', 'victoria', 'liam'].includes(key!)
                        ? <PlaceList places={places} personFilter={key!.charAt(0).toUpperCase() + key!.slice(1)} onPlaceClick={onPlaceClick} />
                        : <PlaceList places={places} filter={key === 'places' ? undefined : key as VisitType} onPlaceClick={onPlaceClick} />
                    }
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              Legend
            </div>
            {[
              { color: PERSON_COLORS.Mark,     label: 'Mark' },
              { color: PERSON_COLORS.Anya,     label: 'Anya' },
              { color: PERSON_COLORS.Victoria, label: 'Victoria' },
              { color: PERSON_COLORS.Liam,     label: 'Liam' },
              { color: MARKER_COLORS.family,   label: 'Family' },
              { color: MARKER_COLORS.group,    label: 'Group' },
              { color: MARKER_COLORS.markanya, label: 'Mark & Anya' },
            ].map(({ color, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#424242' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
